import { IBusinessOption } from '@components'
import { useBusinessContext } from '@context'
import { fetchBusiness, IBusiness, useCurrentUser } from '@query'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

const toBusinessOption = (business: IBusiness): IBusinessOption => ({
  id: business.id,
  slug: business.slug,
  name: business.name
})

export const useNotificationPrefsBusiness = (isVisible: boolean) => {
  const { data: contextBusiness } = useBusinessContext()
  const { data: user } = useCurrentUser()
  // undefined: not touched yet (use the current business); null: the user cleared the selector
  const [picked, setPicked] = useState<IBusinessOption | null | undefined>(undefined)
  const [wasVisible, setWasVisible] = useState(isVisible)

  if (isVisible !== wasVisible) {
    setWasVisible(isVisible)
    if (isVisible) {
      setPicked(undefined)
    }
  }

  const usershipCount = user?.userships.length ?? 0
  const showSelector = usershipCount > 1
  const soleUsership = usershipCount === 1 ? user?.userships[0] : undefined
  const soleSlug = soleUsership?.business_id
  const needsSoleDefault = !contextBusiness && picked === undefined && Boolean(soleSlug)

  const soleBusinessQuery = useQuery({
    queryKey: ['business', soleSlug],
    queryFn: () => fetchBusiness({ slug: soleSlug! }),
    enabled: needsSoleDefault && isVisible && Boolean(soleSlug)
  })

  const selectedBusiness: IBusinessOption | null =
    picked !== undefined
      ? picked
      : contextBusiness
      ? toBusinessOption(contextBusiness)
      : soleBusinessQuery.data
      ? toBusinessOption(soleBusinessQuery.data)
      : null

  const slug = selectedBusiness?.slug ?? ''

  const isResolvingSoleBusiness = needsSoleDefault && soleBusinessQuery.isPending
  const soleBusinessResolveError = needsSoleDefault && soleBusinessQuery.isError

  return {
    showSelector,
    hasBusinesses: usershipCount > 0,
    selectedBusiness,
    slug,
    changeBusiness: setPicked,
    isResolvingSoleBusiness,
    soleBusinessResolveError,
    retrySoleBusinessResolve: soleBusinessQuery.refetch
  }
}
